<template>
  <form
    class="klaviyo-bis-form-wrapper"
    :disabled="state === 'submitting'"
    @submit.prevent="submitBackInStockForm"
  >
    <div
      class="klaviyo-bis-form"
      :class="{
        [$class('disabled')]: state === 'submitting',
      }"
    >
      <btn
        v-if="!showBackInStockForm"
        class="klaviyo-bis-form__display-btn"
        :label="displayText || $string('product.back_in_stock.email_notify')"
        type="button"
        modifiers="block"
        @click="showBackInStockForm = true"
      />

      <template v-else-if="state === 'submitted'">
        <p
          class="klaviyo-bis-form__success text-body-2xs-regular"
          v-text="$string('product.back_in_stock.success')"
        />
      </template>

      <template v-else>
        <label
          :for="`${namespace}-bis-email`"
          :class="$class('hidden')"
          v-text="$string('product.back_in_stock.email_placeholder')"
        />

        <input
          :id="`${namespace}-bis-email`"
          ref="backInStockEmail"
          v-model="backInStockEmail"
          type="email"
          required
          autocomplete="off"
          class="klaviyo-bis-form__input text-body-2xs-regular"
          :placeholder="placeholderText || $string('product.back_in_stock.email_placeholder')"
        >
      </template>
    </div>

    <div>
      <button
        ref="formSubmitButton"
        class="klaviyo-bis-form-wrapper__btn button button--primary button--block button--center"
        type="submit"
      >
        <span>
          <icon-envelope />
        </span>
        <span v-text="$string('product.form.notify_me')" />
      </button>
    </div>
  </form>
  <div class="klaviyo-bis-drawer__subscription-form">
    <klaviyo-sign-up-form
      v-if="cnvs?.credentials?.klaviyo?.listId"
      :id="`${namespace}--${variant?.id}`"
      ref="klaviyoMarketingForm"
      :custom-email="klaviyoSignUpEmail"
      :source="`Back in Stock ${namespace}`"
      :list-id="cnvs.credentials.klaviyo.listId"
    />
  </div>
</template>

<script>

/**
 * Vue: Klaviyo back in stock (klaviyo-bis-form)
 * -----------------------------------------------------------------------------
 * Klaviyo back in stock form.
 *
 * @param {String} acceptsMarketing - User accepts marketing
 * @param {String} displayText - Text shown in Klaviyo toggle form CTA
 * @param {String} namespace - Namespace of parent component
 * @param {Boolean|Null|Object} product - Canvas formatted product object.
 * @param {Boolean|Null|Object} variant - Canvas formatted variant object.
 *
 */
import Btn from '~global/btn/btn'
import IconEnvelope from '~icons/general/envelope.svg'
import KlaviyoSignUpForm from '~async/klaviyo-sign-up-form/klaviyo-sign-up-form'

export default {
  name: 'KlaviyoBisForm',

  components: {
    Btn,
    IconEnvelope,
    KlaviyoSignUpForm,
  },

  props: {
    acceptsMarketing: {
      type: Boolean,
      default: false,
    },
    displayText: {
      type: String,
      default: '',
    },
    placeholderText: {
      type: String,
      default: '',
    },
    namespace: {
      type: String,
      default: 'klaviyo-bis-form',
    },
    product: {
      type: [Boolean, null, Object],
      default: false,
      required: true,
    },
    variant: {
      type: [Boolean, null, Object],
      default: false,
      required: true,
    },
  },

  emits: {
    'update-email': (payload) => typeof payload === 'string',
    'form-submitted': () => true,
  },

  data() {
    return {
      cnvs: window.cnvs,
      state: 'ready',
      backInStockEmail: '',
      klaviyoSignUpEmail: '',
      showBackInStockForm: false,
    }
  },

  watch: {

    /**
     * Emit back in stock email on update
     */
    backInStockEmail() {
      this.$emit('update-email', this.backInStockEmail)
    },

    /**
     * Set focus on form input field
     */
    async showBackInStockForm() {
      if (!this.showBackInStockForm) {
        return
      }

      await this.$nextTick()
      this.$refs.backInStockEmail?.focus()
    },
  },

  methods: {

    /**
     * Handle back in stock form submission
     */
    submitBackInStockForm() {
      if (this.state === 'submitting') {
        return
      }

      this.state = 'submitting'
      const { token, version } = this.$variable('credentials.klaviyo')

      const options = {
        method: 'POST',
        headers: {
          accept: 'application/json',
          revision: version,
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: this.backInStockEmail,
                  },
                },
              },
              channels: ['EMAIL'],
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${this.variant.id}`,
                },
              },
            },
          },
        }),
      }

      fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${token}`, options)
        .then(
          () => {
            this.state = 'submitted'
            this.$emit('form-submitted')

            if (window.htevents) {
              window.htevents.track(
                'Notify Me When Available Submitted',
                {
                  name: this.product?.title,
                  price: (this.product?.price?.amount || 0) / 100,
                  sku: this.variant.sku,
                  shopify_variant_id: this.variant?.id,
                  shopify_product_id: this.product?.id,
                  variant: this.variant.title,
                  category: this.product?.type,
                  compare_at_price: ((this.product?.compareAtPrice?.amount || 0) / 100) || null,
                  currency: window.Shopify.currency.active,
                  email: this.backInStockEmail,
                },
                { sent_from: 'Hightouch JS' },
              )

              window.htevents.identify({
                email: this.backInStockEmail,
              }, { sent_from: 'Hightouch JS' })
            }
          },
        )
        .catch(
          err => {
            this.state = 'error'
            console.error(err)
          },
        )

      if (!this.acceptsMarketing) {
        return
      }

      this.subscribeToMarketing()
    },

    /**
     * Subscribe the email to marketing through child component
     */
    subscribeToMarketing() {
      this.klaviyoSignUpEmail = this.backInStockEmail

      if (!this.$refs.klaviyoMarketingForm) {
        return
      }

      this.$refs.klaviyoMarketingForm?.forceEmailRegistration()
    },

    /**
     * Programmatically submit form
     */
    submitForm() {
      this.$refs.formSubmitButton?.click()
    },
  },
}
</script>

<style lang="scss">
@import './klaviyo-bis-form';
</style>
